import { useAuth } from "@/app/providers/AuthProvider";
import { Bell, Search, ChevronDown, LogOut, Settings } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function DesktopHeader() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      toast.info("Signing out...", {
        description: "Please wait while we end your session",
      });

      await signOut();

      toast.success("Signed out successfully", {
        description: "You have been logged out of your account.",
        icon: "👋",
      });

      navigate("/login");
    } catch (error) {
      toast.error("Failed to sign out", {
        description: error instanceof Error ? error.message : "An error occurred during logout",
      });
    }
  };

  return (
    <header className="hidden h-16 items-center justify-between border-b border-[var(--border)] bg-[var(--card)] px-6 md:flex">
      {/* Left: Search */}
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-64 items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--muted)] px-3 text-sm text-[var(--color-mute)] transition-colors hover:border-[var(--color-hairline-strong)]">
          <Search className="h-4 w-4" />
          <span>Search tasks, people...</span>
          <kbd className="ml-auto rounded border border-[var(--border)] bg-[var(--card)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-mute)]">
            ⌘K
          </kbd>
        </div>
      </div>

      {/* Right: User menu */}
      <div className="flex items-center gap-3">
        <NotificationBell aria-label="Open notifications" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button aria-label="Open account menu" className="flex items-center gap-1.5 rounded-[var(--radius-md)] p-1.5 transition-colors hover:bg-[var(--muted)]">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-primary)] text-xs font-semibold text-white">
                {user?.name?.charAt(0)?.toUpperCase() ?? "U"}
              </div>
              <ChevronDown className="h-4 w-4 text-[var(--color-mute)]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{user?.name ?? "User"}</p>
                <p className="truncate text-xs font-normal text-[var(--muted-foreground)]">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <Settings className="h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem variant="destructive" onClick={handleSignOut}>
              <LogOut className="h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
